import React, { useEffect, useRef, useState } from "react"
import { ChevronLeft, ChevronRight, Images, Loader2 } from "lucide-react"
import styles from "./StarterKitThumbnailSelector.module.scss"
import {
  STARTER_KIT_IMAGES,
  STARTER_KIT_SOURCE_LABEL,
  getStarterKitImageUrl,
  type StarterKitImage,
} from "../../../config/starterKit.ts"
import { urlToFile } from "../../../utils/urlToFile.ts"

type StarterKitSelectorMode = "single" | "multiple"
type StarterKitSelectorVariant = "inline" | "tile"

interface StarterKitThumbnailSelectorProps {
  mode: StarterKitSelectorMode
  onSelect: (file: File) => void
  onOpenCatalog?: () => void
  variant?: StarterKitSelectorVariant
  previewCount?: number
  maxImages?: number
  currentImagesCount?: number
  showBorder?: boolean
  hideHeader?: boolean
  excludeFileNames?: string[]
}

export default function StarterKitThumbnailSelector({
  mode,
  onSelect,
  onOpenCatalog,
  variant = "inline",
  previewCount = 6,
  maxImages,
  currentImagesCount,
  showBorder = true,
  hideHeader = false,
  excludeFileNames,
}: StarterKitThumbnailSelectorProps) {
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const excludeSet = new Set(excludeFileNames ?? [])
  const effectivePreviewCount = variant === "tile" ? Math.min(previewCount, 6) : previewCount
  const previewItems = STARTER_KIT_IMAGES.filter((item) => {
    const fileName = item.path.split("/").pop() || ""
    return fileName ? !excludeSet.has(fileName) : true
  }).slice(0, effectivePreviewCount)
  const effectiveMax = typeof maxImages === "number" ? maxImages : 5
  const effectiveCurrent = typeof currentImagesCount === "number" ? currentImagesCount : 0
  const atLimit = mode === "multiple" ? effectiveCurrent >= effectiveMax : false
  const remaining = mode === "multiple" ? Math.max(0, effectiveMax - effectiveCurrent) : null

  const handlePick = async (item: StarterKitImage) => {
    setError(null)

    if (mode === "multiple" && atLimit) {
      setError(`Máximo ${effectiveMax} imágenes permitidas`)
      return
    }

    setLoadingId(item.id)
    try {
      const url = getStarterKitImageUrl(item)
      const name = item.path.split("/").pop() || "starter-image.jpg"
      const file = await urlToFile(url, name)
      onSelect(file)
    } catch (e) {
      setError("No se pudo cargar la imagen. Intena con otra.")
    } finally {
      setLoadingId(null)
    }
  }

  const gridDisabled = !!loadingId || (mode === "multiple" && atLimit)

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return

    const update = () => {
      const left = el.scrollLeft
      const right = el.scrollWidth - (el.scrollLeft + el.clientWidth)
      setCanScrollLeft(left > 5)
      setCanScrollRight(right > 5)
    }

    update()
    el.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", update)

    return () => {
      el.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [])

  const rootClass = `${showBorder ? styles.root : styles.noBorderRoot} ${variant === "tile" ? styles.tile : styles.inline}`
  const scrollCarousel = (direction: -1 | 1) => {
    const el = scrollerRef.current
    if (!el) return
    const amount = el.clientWidth * 0.9
    el.scrollBy({ left: direction * amount, behavior: "smooth" })
  }

  return (
    <div className={rootClass}>
      {!hideHeader && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Images size={18} />
            <div className={styles.headerText}>
              <div className={styles.title}>{mode === "single" ? "Ejemplo para tu post" : "Imágenes de ejemplo"}</div>
              <div className={styles.subtitle}>
                {mode === "single"
                  ? `De ${STARTER_KIT_SOURCE_LABEL}`
                  : atLimit
                    ? "Máximo alcanzado"
                    : `Elegí hasta ${remaining} más`}
              </div>
            </div>
          </div>
          {onOpenCatalog && variant === "inline" && (
            <button type="button" className={styles.catalogButton} onClick={onOpenCatalog}>
              Ver catálogo
            </button>
          )}
        </div>
      )}

      {!hideHeader && onOpenCatalog && variant === "tile" && (
        <div className={styles.tileCatalogRow}>
          <button type="button" className={styles.catalogTileButton} onClick={onOpenCatalog}>
            Ver catálogo
          </button>
        </div>
      )}

      {variant === "tile" ? (
        <div className={styles.grid} aria-label="Selector de imágenes de ejemplo">
          {previewItems.map((item) => {
            const isLoading = loadingId === item.id
            return (
              <button
                key={item.id}
                type="button"
                className={styles.thumb}
                onClick={() => handlePick(item)}
                disabled={gridDisabled || isLoading}
                aria-label={item.label || item.id}
              >
                <img src={getStarterKitImageUrl(item)} alt={item.label || item.id} />
                {isLoading && (
                  <div className={styles.loadingOverlay}>
                    <Loader2 size={22} className={styles.spinner} />
                  </div>
                )}
              </button>
            )
          })}
        </div>
      ) : (
        <div className={styles.carouselWrap}>
          <button
            type="button"
            className={`${styles.navButton} ${styles.navLeft}`}
            disabled={!canScrollLeft}
            onClick={() => scrollCarousel(-1)}
            aria-label="Anterior"
          >
            <ChevronLeft size={18} />
          </button>
          <div
            className={`${styles.fadeLeft} ${canScrollLeft ? "" : styles.fadeHidden}`}
            aria-hidden="true"
          />
          <button
            type="button"
            className={`${styles.navButton} ${styles.navRight}`}
            disabled={!canScrollRight}
            onClick={() => scrollCarousel(1)}
            aria-label="Siguiente"
          >
            <ChevronRight size={18} />
          </button>
          <div
            className={`${styles.fadeRight} ${canScrollRight ? "" : styles.fadeHidden}`}
            aria-hidden="true"
          />
          <div ref={scrollerRef} className={styles.carousel} aria-label="Selector de imágenes de ejemplo">
            {previewItems.map((item) => {
              const isLoading = loadingId === item.id
              return (
                <button
                  key={item.id}
                  type="button"
                  className={styles.thumb}
                  onClick={() => handlePick(item)}
                  disabled={gridDisabled || isLoading}
                  aria-label={item.label || item.id}
                >
                  <img src={getStarterKitImageUrl(item)} alt={item.label || item.id} />
                  {isLoading && (
                    <div className={styles.loadingOverlay}>
                      <Loader2 size={22} className={styles.spinner} />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}
    </div>
  )
}

